// Faz 40 PR-A — Pilot Deploy Status view.
// Pilot rol netleştirmesi: Pilot SSH yapmaz, deploy.sh çalıştırmaz; Baş Yılmaz
// Telegram /deploy ile tetikler. Bu panel post-deploy izleme: son deploy +
// smoke test + container health + öneriler. Endpoint: GET /castintech-api/pilot/deploy-status
// (60s server cache, requireAuth bearer|cookie).
// ADR: wiki/decisions/faz40-pr-a-pilot-rol-clarification.md

import { apiFetch } from '../api.js';
import { escHtml, escAttr, ago, $ } from '../utils.js';
import { renderError as dsRenderError, renderEmpty, createSkeleton } from '../ui/state-display.js';

interface DeployRow {
  ts: string;
  actor: string;
  reason: string;
  host: string;
  status: string;
  raw?: string;
}

interface SmokeResult {
  ts: string | null;
  action: string;
  status: string;
  ok_count: number;
  fail_count: number;
  message: string;
}

interface HealthBlock {
  ok: boolean;
  detail?: string;
}

interface HealthSummary {
  notify: HealthBlock;
  paperclip: HealthBlock;
  postgres: HealthBlock;
}

interface PilotStatusPayload {
  last_deploy: DeployRow | null;
  smoke_result: SmokeResult | null;
  health: HealthSummary;
  recommendations: string[];
  generated_at: string;
  cached?: boolean;
}

let cache: PilotStatusPayload | null = null;

export async function loadPilotDeployStatus(): Promise<void> {
  // F4-A — İlk yüklemede skeleton.
  const root = $('pilot-deploy-status-content');
  if (root && !cache) {
    root.innerHTML = '';
    const sk = createSkeleton({ rows: 4, height: '56px', rowGap: '12px' });
    root.appendChild(sk);
  }
  try {
    const r = await apiFetch('/castintech-api/pilot/deploy-status');
    if (!r.ok) throw new Error('pilot/deploy-status HTTP ' + r.status);
    cache = (await r.json()) as PilotStatusPayload;
    renderPilotDeployStatus();
  } catch (e) {
    console.warn('loadPilotDeployStatus failed:', (e as Error).message);
    if (root) {
      // F4-A — state-display.ts renderError + Türkçe karakter düzeltmesi.
      dsRenderError(root, e as Error, {
        title: 'Pilot deploy durumu yüklenemedi',
        description: 'Bağlanırken sorun çıktı: ' + (e as Error).message,
        onRetry: () => { void loadPilotDeployStatus(); },
      });
    }
  }
}

function statusBadge(status: string): string {
  const s = String(status || '').toLowerCase();
  if (s === 'pass' || s.startsWith('ok')) return '<span class="audit-badge ok">PASS</span>';
  if (s === 'fail' || s.startsWith('abort')) return '<span class="audit-badge err">FAIL</span>';
  if (s.startsWith('force')) return '<span class="audit-badge warn">FORCE</span>';
  return `<span class="audit-badge muted">${escHtml(status || '?')}</span>`;
}

function healthBadge(label: string, h: HealthBlock): string {
  const cls = h.ok ? 'ok' : 'err';
  const txt = h.ok ? 'UP' : 'DOWN';
  const detail = h.detail ? ` <span class="muted">(${escHtml(h.detail)})</span>` : '';
  return `<span class="audit-badge ${cls}">${escHtml(label)} ${txt}</span>${detail}`;
}

export function renderPilotDeployStatus(): void {
  const root = $('pilot-deploy-status-content');
  const sub = $('pilot-deploy-status-sub');
  if (!root) return;
  if (!cache) {
    // F4-A — Empty state: state-display.ts renderEmpty + Türkçe karakter.
    renderEmpty(root, {
      icon: '🛬',
      title: 'Henüz deploy verisi yok',
      description: 'Pilot deploy raporu üretmedi. /deploy komutu çalıştırıldığında burada görünür.',
    });
    return;
  }
  if (sub) {
    const cachedNote = cache.cached ? ' (cache)' : '';
    sub.textContent = `Son guncelleme: ${ago(cache.generated_at)} once${cachedNote} · Pilot SSH yapmaz, sadece izler`;
  }

  // Son deploy kartı
  let deployCard = '<div class="rt-card"><div class="rt-card-title">Son Deploy</div>';
  if (cache.last_deploy) {
    const d = cache.last_deploy;
    deployCard += `
      <div class="audit-row">
        ${statusBadge(d.status)}
        <div class="audit-body">
          <div class="audit-msg"><b>${escHtml(d.actor || '?')}</b> · ${escHtml(d.reason || '')}</div>
          <div class="audit-meta-line">host=${escHtml(d.host || '?')}</div>
        </div>
        <span class="audit-time" title="${escAttr(d.ts || '')}">${ago(d.ts || '')} once</span>
      </div>`;
  } else {
    deployCard += '<div class="audit-empty">Deploy log bos veya bulunamadi.</div>';
  }
  deployCard += '</div>';

  // Smoke test kartı
  let smokeCard = '<div class="rt-card"><div class="rt-card-title">Smoke Test Sonucu</div>';
  if (cache.smoke_result) {
    const s = cache.smoke_result;
    const totalNote = s.ok_count + s.fail_count > 0
      ? ` · ${s.ok_count}/${s.ok_count + s.fail_count} PASS`
      : '';
    smokeCard += `
      <div class="audit-row">
        ${statusBadge(s.status)}
        <div class="audit-body">
          <div class="audit-msg">${escHtml(s.action)}${escHtml(totalNote)}</div>
          <div class="audit-meta-line">${escHtml(s.message)}</div>
        </div>
        <span class="audit-time" title="${escAttr(s.ts || '')}">${ago(s.ts || '')} once</span>
      </div>`;
  } else {
    smokeCard += '<div class="audit-empty">Henuz smoke test audit kaydi yok.</div>';
  }
  smokeCard += '</div>';

  // Container health kartı
  const h = cache.health;
  const healthCard = `
    <div class="rt-card">
      <div class="rt-card-title">Container Health</div>
      <div style="display:flex;flex-wrap:wrap;gap:8px;align-items:center;padding:8px 0;">
        ${healthBadge('notify', h.notify)}
        ${healthBadge('paperclip', h.paperclip)}
        ${healthBadge('postgres', h.postgres)}
      </div>
    </div>`;

  // Öneriler
  let recCard = '<div class="rt-card"><div class="rt-card-title">Oneriler (Pilot rapor taslagi)</div>';
  if (cache.recommendations.length === 0) {
    recCard += '<div class="audit-empty">Tum sistemler yesil. Onerilebilecek aksiyon yok.</div>';
  } else {
    recCard += '<ul style="margin:6px 0;padding-left:20px;">';
    for (const r of cache.recommendations) {
      recCard += `<li>${escHtml(r)}</li>`;
    }
    recCard += '</ul>';
  }
  recCard += '</div>';

  root.innerHTML = deployCard + smokeCard + healthCard + recCard;
}
